/**
 * One line of a profile section: the field's label, its value, where the value came from, and what
 * a person can do about it.
 *
 * Expects the value and its catalog entry, the callback the page decides a value with, the notice
 * the page put on this line, busy while a write is in flight, and the callback that opens a source
 * document.
 *
 * Renders one table row of five cells:
 *  - the label, with a yellow left edge while the value waits for a person ('proposed' or
 *    'changed');
 *  - the value as a button: an approved value in bold, a proposed value in italics, "Not on file"
 *    for an empty one; a value that waits has a dashed yellow border (the theme's warning color,
 *    as the left edge), and the others none. Clicking it opens ValuePicker under it, on every value, an approved one
 *    included, so a person changes an approved value there. The button's accessible name ends with
 *    waitingTag ("Proposed", "Proposed · 2 values", "Changed · 1 new value");
 *  - the yellow tag waitingPill gives ("2 values", "1 new value"), which never wraps, or the green
 *    check (accessible name "Approved") of an approved value, or nothing: a proposed value with one
 *    waiting value has no tag, because its yellow left edge, italic value and Accept button already
 *    say it waits. The tag is plain text, not a second control;
 *  - the Where it came from sentence (whereItCameFrom in wording.ts), with each document's name a
 *    link that opens the document;
 *  - the Approval cell: while the page keeps a notice for the line, the notice and its Undo; else a
 *    green outlined Accept on a proposed value whose sources all give one value, which accepts it
 *    in one click; a yellow Choose value (proposed, several values) or Review conflict (changed),
 *    which opens ValuePicker; "Approved by <person> on <date>" in green on an approved value;
 *    nothing on an empty one. From lg up the cell's content is centered, below lg right-aligned.
 *
 * From Tailwind's lg width (1024px) up the row is an ordinary table row and the cells are the
 * columns of ProfileGroup's table. Below it the row is a three-column grid (FIELD_TABLE_LAYOUT):
 * the label on the left, the value with the tag and the Where it came from sentence stacked under
 * it in the middle, and the Approval cell on the right, so no column is squeezed to a few pixels.
 *
 * This component writes nothing itself; it says which decision was taken and the page writes it.
 */

import { useCallback, useRef, useState } from 'react';
import { faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { TaxProfileFieldDefinition } from '@shared/constants/taxProfileFields';
import type { TaxProfileFieldValue } from '@shared/types/taxProfile';
import { formatFieldValue } from '@/pages/TaxProfile/format';
import { fieldNeedsReview, shownValue } from '@/pages/TaxProfile/fieldState';
import {
  VALUE_WORDS,
  approvedSentence,
  waitingPill,
  waitingTag,
  whereItCameFrom,
} from '@/pages/TaxProfile/wording';
import SourceText from '@/pages/TaxProfile/components/SourceText';
import ValuePicker from '@/pages/TaxProfile/components/ValuePicker';
import type { FieldDecision } from '@/pages/TaxProfile/components/ValuePicker';

/**
 * What the line shows after an act, and what taking that act back does.
 *
 * `onUndo` is left out for an act there is nothing to take back.
 */
export type FieldRowNotice = { message: string; onUndo?: () => void };

export interface FieldRowProps {
  field: TaxProfileFieldValue;
  definition: TaxProfileFieldDefinition;
  /** Writes what a person decided: accept a candidate, keep the approved value, or a typed value. */
  onDecide?: (
    field: TaxProfileFieldValue,
    definition: TaxProfileFieldDefinition,
    decision: FieldDecision
  ) => void;
  /** Opens the file of a document named on the line or in its panel. */
  onOpenSourceDocument?: (documentId: number) => void;
  /** What was just done to this value and how to undo it; gone after eight seconds. */
  notice?: FieldRowNotice | null;
  /** True while a write for this value is in flight: the line's buttons wait for it. */
  busy?: boolean;
  /** True for the moment after "Go to field" on a document's summary screen sent the page here. */
  highlighted?: boolean;
}

/** The id of a value's row, which "Go to field" on a document's summary screen scrolls to. */
export const fieldRowElementId = (key: string) =>
  `tax-profile-field-row-${key}`;

/**
 * A notice and its Undo, as the Approval cell of a FieldRow, of a ListTable row and of the person
 * window draw it. Undo is disabled while `busy`: a write is in flight, and Undo would send a second
 * write against a value the first one may still change.
 */
export function NoticeText({
  notice,
  busy = false,
}: {
  notice: FieldRowNotice;
  busy?: boolean;
}) {
  return (
    <span className='text-xs opacity-80'>
      {notice.message}
      {notice.onUndo && (
        <>
          {' · '}
          <button
            type='button'
            className='btn btn-xs btn-ghost text-primary'
            disabled={busy}
            onClick={notice.onUndo}
          >
            {VALUE_WORDS.undo}
          </button>
        </>
      )}
    </span>
  );
}

/**
 * The value's button: the faint outline and the focus ring; FieldRow adds the dashed border, in the
 * warning yellow of the line's left edge, on a value that waits for a person. The
 * button wraps (flex-wrap), so the chevron moves under a long value instead of taking width from it.
 */
const VALUE_BUTTON_CLASS =
  'inline-flex flex-wrap max-w-full items-center gap-x-2 text-left rounded px-2 py-1 -mx-2 border ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary';

/**
 * The value's text. It wraps between words (break-words breaks inside a word only when that one
 * word is wider than the cell) and it has no min-w-0, so a flex sibling can never shrink it below
 * its longest word, which is what wrapped a value one letter per line.
 */
export const VALUE_TEXT_CLASS = 'break-words max-w-full';

/**
 * The class on the <tr> of a row the page has just sent the reader to: a document's row from the
 * left column (DocumentsTable.tsx), and a value's row or a list row from "Go to field" on a
 * document's summary screen (this file, ListTable.tsx). It tints the row and its cells in the
 * theme's primary blue, the cells too because they carry their own background, and pulses with
 * Tailwind's animate-pulse. TaxProfile.tsx takes it off after ROW_HIGHLIGHT_MS.
 */
export const HIGHLIGHT_ROW_CLASS =
  '!bg-primary/20 [&>td]:!bg-primary/20 animate-pulse';

/**
 * The classes of ProfileGroup's table and of each FieldRow cell and header, for the two layouts:
 * below lg a three-column grid per row (label | value, tag and source stacked | Approval), from lg
 * up a fixed table of five columns whose header widths are set here. Below lg the tag and source
 * cells are hidden while empty, so a line with no tag or no source is not left with a blank band.
 *
 * The cells' z-index is reset to auto, with !important because ClientList's TableClient.css gives
 * every daisyUI table cell `z-index: 1 !important` once that page's module is loaded. It does
 * nothing to a table cell but makes each grid cell below lg its own stacking context, so
 * ValuePicker's popup drew under the cells after it.
 *
 * The table turns back into a table from lg up with lg:[display:table], not lg:table: in this
 * project `table` is also daisyUI's table class, so lg:table repeats every daisyUI table rule inside
 * the lg media query, after Tailwind's utilities, and its `white-space: nowrap` on every cell beat
 * the cells' whitespace-normal. Labels then ran into the next column and the Approval sentence ran
 * past the table's right edge.
 */
export const FIELD_TABLE_LAYOUT = {
  table:
    'table table-zebra table-compact w-full block lg:[display:table] lg:table-fixed [&_td]:!z-auto [&_th]:!z-auto',
  section: 'block lg:table-row-group',
  headSection: 'block lg:table-header-group',
  row: 'grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)_9rem] grid-rows-[1fr_auto_auto] lg:table-row',
  fieldHeader: 'col-start-1 whitespace-normal lg:w-[18%] xl:w-[16%]',
  valueHeader: 'col-start-2 whitespace-normal lg:w-[20%] xl:w-[18%]',
  pillHeader: 'hidden lg:table-cell lg:w-24 lg:text-center',
  sourceHeader: 'hidden lg:table-cell whitespace-normal lg:text-center',
  approvalHeader: 'col-start-3 lg:w-[30%] 2xl:w-[22rem] lg:text-center',
  field:
    'col-start-1 row-start-1 row-span-3 align-middle whitespace-normal break-words border-l-4',
  value:
    'col-start-2 row-start-1 pb-1 lg:pb-2 align-middle whitespace-normal break-words',
  pill:
    'col-start-2 row-start-2 pt-0 pb-1 lg:py-2 align-middle whitespace-normal lg:text-center ' +
    'empty:hidden lg:empty:table-cell',
  source:
    'col-start-2 row-start-3 pt-1 lg:pt-2 align-middle whitespace-normal break-words lg:text-center ' +
    'empty:hidden lg:empty:table-cell',
  approval:
    'col-start-3 row-start-1 row-span-3 align-middle text-right lg:text-center whitespace-normal',
} as const;

export default function FieldRow({
  field,
  definition,
  onDecide,
  onOpenSourceDocument,
  notice = null,
  busy = false,
  highlighted = false,
}: FieldRowProps) {
  const [isOpen, setIsOpen] = useState(false);
  const valueRef = useRef<HTMLButtonElement>(null);

  const waiting = fieldNeedsReview(field);
  const highlight = highlighted ? HIGHLIGHT_ROW_CLASS : '';
  const valueText = formatFieldValue(shownValue(field), definition);
  const tag = waitingTag(field);
  const pill = waitingPill(field);
  /** A proposed value whose sources all give one value, which Accept takes in one click. */
  const singleCandidateRowId =
    field.status === 'proposed' && field.waiting.length === 1
      ? field.waiting[0].sources[0]?.rowId ?? null
      : null;

  const close = useCallback((returnFocus: boolean) => {
    setIsOpen(false);
    if (returnFocus) valueRef.current?.focus();
  }, []);

  const decide = (decision: FieldDecision) => {
    close(true);
    onDecide?.(field, definition, decision);
  };

  let valueClass = '';
  if (field.status === 'proposed') valueClass = 'italic';
  else if (field.status === 'empty') valueClass = 'opacity-60';
  else valueClass = 'font-bold';

  const approvalCell = () => {
    if (notice) {
      return <NoticeText notice={notice} busy={busy} />;
    }
    if (singleCandidateRowId !== null) {
      return (
        <button
          type='button'
          className='btn btn-sm btn-outline btn-accent gap-1'
          disabled={busy}
          onClick={() =>
            onDecide?.(field, definition, {
              kind: 'accept',
              rowId: singleCandidateRowId,
            })
          }
        >
          <FontAwesomeIcon icon={faCheck} />
          {VALUE_WORDS.accept}
        </button>
      );
    }
    if (waiting) {
      return (
        <button
          type='button'
          className='btn btn-sm btn-warning'
          disabled={busy}
          onClick={() => setIsOpen(true)}
        >
          {field.status === 'changed'
            ? VALUE_WORDS.reviewConflict
            : VALUE_WORDS.chooseValue}
        </button>
      );
    }
    if (field.status === 'approved' && field.approved) {
      return (
        <span className='text-xs text-success'>
          {approvedSentence(field.approved)}
        </span>
      );
    }
    return null;
  };

  return (
    <tr
      id={fieldRowElementId(definition.key)}
      data-testid={`tax-profile-field-${definition.key}`}
      data-highlighted={highlighted || undefined}
      className={`${FIELD_TABLE_LAYOUT.row} scroll-mt-4 ${highlight}`}
    >
      <td
        className={`${FIELD_TABLE_LAYOUT.field} ${
          waiting ? 'border-l-warning' : 'border-l-transparent'
        }`}
      >
        {definition.label}
      </td>
      <td className={FIELD_TABLE_LAYOUT.value}>
        <div className='relative'>
          <button
            ref={valueRef}
            type='button'
            aria-haspopup='dialog'
            aria-expanded={isOpen}
            aria-label={`${definition.label}: ${valueText}${
              tag ? `, ${tag}` : ''
            }`}
            className={`${VALUE_BUTTON_CLASS} ${
              waiting
                ? 'border-dashed border-warning'
                : 'border-transparent hover:border-base-300'
            } ${isOpen ? 'bg-base-200' : ''}`}
            disabled={busy}
            onClick={() => setIsOpen((open) => !open)}
          >
            <span className={`${VALUE_TEXT_CLASS} ${valueClass}`}>
              {valueText}
            </span>
            <FontAwesomeIcon
              icon={faChevronDown}
              className='text-xs opacity-50'
            />
          </button>
          {isOpen && (
            <ValuePicker
              field={field}
              definition={definition}
              anchorRef={valueRef}
              onDecide={decide}
              onClose={close}
              onOpenSourceDocument={onOpenSourceDocument}
              busy={busy}
            />
          )}
        </div>
      </td>
      <td className={FIELD_TABLE_LAYOUT.pill}>
        {pill && (
          <span className='badge badge-warning badge-sm whitespace-nowrap'>
            {pill}
          </span>
        )}
        {field.status === 'approved' && field.approved && (
          <span
            role='img'
            aria-label={VALUE_WORDS.approved}
            className='text-success'
          >
            <FontAwesomeIcon icon={faCheck} aria-hidden />
          </span>
        )}
      </td>
      <td className={FIELD_TABLE_LAYOUT.source}>
        <SourceText
          pieces={whereItCameFrom(field, definition.key)}
          onOpenDocument={onOpenSourceDocument}
          className='text-xs opacity-80'
          stacked
        />
      </td>
      <td className={FIELD_TABLE_LAYOUT.approval}>{approvalCell()}</td>
    </tr>
  );
}
